import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import * as React from 'react';
import { rgba } from 'polished';
import styled, { css } from 'styled-components';

import { CloseIcon, EnterIcon } from '../../../icons/Icons';
import { getFontStyle, measureText } from '../../../utils/measureText';

import { type MagicEditBarMode } from './sharedStyles';

const MAGIC_EDIT_BAR_HEIGHT = 40;
const INPUT_HORIZ_PADDING = 10;
const ENTER_ICON_WIDTH = 16;
/** The space between the end of the entered text and the enter icon itself */
const ENTER_ICON_GAP = 5;

const MAGIC_ELEMENT_SIZES: Record<string, number> = {
  Default: 30,
  StillThinking: 94
};

const Wrapper = styled.div<{ $isOpen: boolean }>`
  overflow: hidden;

  box-shadow: inset 0 -1px 0 0 ${({ theme }) => rgba(theme.appUI.magic.primaryColor, 0.07)};

  transition:
    height ${({ theme }) => theme.animation.durationMedium}
      ${({ theme }) => theme.animation.EASING},
    opacity ${({ theme }) => theme.animation.durationMedium}
      ${({ theme }) => theme.animation.EASING};

  height: 0;
  opacity: 0;

  ${({ $isOpen }) =>
    $isOpen &&
    css`
      height: ${MAGIC_EDIT_BAR_HEIGHT}px;
      opacity: 1;
    `}
`;

const Inner = styled.div<{ $stillThinking?: boolean }>`
  display: flex;
  align-items: center;
  height: ${MAGIC_EDIT_BAR_HEIGHT}px;
  padding: 0 0 0 10px;
  padding-right: ${({ $stillThinking }) =>
    $stillThinking
      ? MAGIC_ELEMENT_SIZES.StillThinking + 10
      : MAGIC_ELEMENT_SIZES.Default + 10}px;

  background: linear-gradient(
    90deg,
    ${({ theme }) => rgba(theme.appUI.magic.primaryColor, 0.06)} 0%,
    ${({ theme }) => rgba(theme.appUI.magic.secondaryColor, 0.025)} 100%
  );

  transition: padding ${({ theme }) => theme.animation.durationMedium}
    ${({ theme }) => theme.animation.EASING};
`;

const ModeLabel = styled.div`
  margin-left: 5px;

  color: ${({ theme }) => theme.appUI.magic.primaryColor};
  font-weight: ${({ theme }) => theme.fontWeight.BOLD};
  font-size: ${({ theme }) => theme.fontSize.EXTRA_SMALL};
  text-transform: uppercase;

  cursor: pointer;
`;

const InputContainer = styled.div`
  position: relative;

  display: flex;
  flex: 1 1 auto;
  /* shift input up a hair to keep in line with magic icon and mode label */
  margin-top: -1px;
`;

const InvisibleInput = styled.input`
  flex: 1 1 auto;
  width: 100%;
  margin-right: ${ENTER_ICON_GAP + ENTER_ICON_WIDTH - INPUT_HORIZ_PADDING}px;
  padding: 7px ${INPUT_HORIZ_PADDING}px;

  color: ${({ theme }) => theme.appUI.magic.primaryColor};
  font-family: ${({ theme }) => theme.fontFamily.SANS};

  &,
  &:focus,
  &:disabled {
    background: transparent;
    border: none;
    outline: none;
    box-shadow: none;
  }

  &::placeholder {
    color: ${({ theme }) => rgba(theme.appUI.magic.primaryColor, 0.5)};
  }
`;

const FloatingEnterIcon = styled.div`
  position: absolute;

  align-self: center;

  margin-left: ${ENTER_ICON_GAP}px;

  svg {
    color: ${({ theme }) => rgba(theme.appUI.magic.primaryColor, 0.4)};
  }
`;

const CloseButton = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    color: ${({ theme }) => rgba(theme.appUI.magic.primaryColor, 0.4)};
  }
`;

interface MagicEditBarProps {
  isOpen: boolean;
  mode: MagicEditBarMode;
  onClose: () => void;
  promptText?: string;
}

export const MagicEditBar: React.ComponentType<MagicEditBarProps> = React.memo(
  ({ isOpen = false, onClose, promptText, mode = 'generate' }) => {
    const [currentPrompt, setCurrentPrompt] = useState('');

    // Properties for tracking current text width
    const fontStyle = useRef<string | undefined>(undefined);
    const inputRef = useRef<HTMLInputElement>(null);
    const [inputTextWidth, setInputTextWidth] = useState<number | undefined>(
      undefined
    );

    useLayoutEffect(() => {
      if (inputRef.current == null) return;

      // The input's font style shouldn't ever change, so we only compute this on the first run
      fontStyle.current ??= getFontStyle(inputRef.current);

      if (fontStyle.current) {
        const textWidth = measureText(currentPrompt, fontStyle.current);
        setInputTextWidth(textWidth);
      }
    }, [currentPrompt]);

    // Update if incoming promptText
    useEffect(() => {
      if (promptText !== undefined) {
        setCurrentPrompt(promptText);
      }
    }, [promptText]);

    const enterIconLeft =
      inputTextWidth != null ? INPUT_HORIZ_PADDING + inputTextWidth : undefined;

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setCurrentPrompt(e.currentTarget.value);
      },
      []
    );

    return (
      <Wrapper $isOpen={isOpen}>
        <Inner>
          <ModeLabel>{mode === 'generate' ? 'GENERATE' : 'EDIT'}</ModeLabel>
          <InputContainer>
            <InvisibleInput
              ref={inputRef}
              disabled={true}
              placeholder={
                mode === 'generate'
                  ? 'What do you want to add?'
                  : 'How do you want to edit this cell?'
              }
              value={currentPrompt}
              onChange={handleChange}
            />
            {enterIconLeft != null && currentPrompt !== '' && (
              <FloatingEnterIcon
                style={{
                  left: `min(calc(100% - ${ENTER_ICON_WIDTH}px - ${ENTER_ICON_GAP}px), ${enterIconLeft}px)`
                }}>
                <EnterIcon />
              </FloatingEnterIcon>
            )}
          </InputContainer>
          <CloseButton onClick={onClose}>
            <CloseIcon />
          </CloseButton>
        </Inner>
      </Wrapper>
    );
  }
);
